import { apiClient, ApiResponse, extractData } from './api'
import type {
  CheckoutTemplate,
  Product,
  Discount,
  Order,
  CheckoutFormData,
  CustomerPrefill,
} from '@/types/checkout'

export interface CheckoutTemplateResponse {
  template: CheckoutTemplate
  product: Product
  discount?: Discount
  customer_prefill?: CustomerPrefill
}

export interface ProcessCheckoutRequest extends CheckoutFormData {
  template_id: number
  product_id: number
  metadata?: Record<string, any>
}

export interface ProcessCheckoutResponse {
  order: Order
  next_step: {
    action: 'payment'
    payment_method: string
  }
}

export const checkoutService = {
  /**
   * Carregar template do checkout
   */
  async getTemplate(
    slug: string,
    queryParams?: Record<string, string>
  ): Promise<CheckoutTemplateResponse> {
    const response = await apiClient.get<ApiResponse<CheckoutTemplateResponse>>(
      `/checkout/${slug}`,
      { params: queryParams }
    )
    return extractData(response)
  },

  /**
   * Validar dados antes de processar
   */
  async validate(data: ProcessCheckoutRequest): Promise<{ valid: boolean; order_summary: any }> {
    const response = await apiClient.post<ApiResponse<{ valid: boolean; order_summary: any }>>('/checkout/validate', data)
    return extractData(response)
  },

  /**
   * Processar checkout e criar ordem
   */
  async process(data: ProcessCheckoutRequest): Promise<ProcessCheckoutResponse> {
    const response = await apiClient.post<ApiResponse<ProcessCheckoutResponse>>(
      '/checkout/process',
      data
    )
    return extractData(response)
  },

  /**
   * Consultar status de uma ordem
   */
  async getOrder(orderId: string): Promise<Order> {
    const response = await apiClient.get<ApiResponse<{ order: Order }>>(`/checkout/order/${orderId}`)
    const data = extractData(response)
    return data.order
  },

  /**
   * Processar upsell
   */
  async processUpsell(originalOrderId: string, upsellProductId: number): Promise<Order> {
    const response = await apiClient.post<ApiResponse<{ order: Order }>>('/checkout/upsell', {
      original_order_id: originalOrderId,
      upsell_product_id: upsellProductId,
    })
    const data = extractData(response)
    return data.order
  },

  /**
   * Processar downsell
   */
  async processDownsell(originalOrderId: string, downsellProductId: number): Promise<Order> {
    const response = await apiClient.post<ApiResponse<{ order: Order }>>('/checkout/downsell', {
      original_order_id: originalOrderId,
      downsell_product_id: downsellProductId,
    })
    const data = extractData(response)
    return data.order
  },
}
